// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type TemplateVersion = {
  version: number;
  status: "draft" | "published";
  schemaHash: string;
  changeNote: string | null;
  createdBy: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
};
export const TemplateVersionSchema = z.object({
  version: z.number().int(),
  status: z.enum(["draft", "published"] as const),
  schemaHash: z.string(),
  changeNote: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
}).strict();

export type TemplateVersionDetail = {
  version: number;
  status: "draft" | "published";
  schemaHash: string;
  changeNote: string | null;
  createdBy: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  schemaSnapshot: unknown;
  templateHtml: string | null;
  templateCss: string | null;
};
export const TemplateVersionDetailSchema = z.object({
  version: z.number().int(),
  status: z.enum(["draft", "published"] as const),
  schemaHash: z.string(),
  changeNote: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  schemaSnapshot: z.unknown(),
  templateHtml: z.string().nullable(),
  templateCss: z.string().nullable(),
}).strict();

export type TemplateAction = {
  action: "edit" | "submit" | "approve" | "reject" | "publish" | "archive" | "restore";
  allowed: boolean;
  reason: string | null;
};
export const TemplateActionSchema = z.object({
  action: z.enum(["edit", "submit", "approve", "reject", "publish", "archive", "restore"] as const),
  allowed: z.boolean(),
  reason: z.string().nullable(),
}).strict();

export type Template = {
  _id: string;
  organizationId: string;
  name: string;
  description: string | null;
  status: "draft" | "in_review" | "published" | "archived";
  publishedVersion: number | null;
  draftVersion: number | null;
  revision: number;
  origin: "built" | "imported" | "starter" | "agent";
  domainKey: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  versions: Array<TemplateVersion>;
  reviewRequired: boolean;
  submittedBy: string | null;
  submittedAt: string | null;
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  actions: Array<TemplateAction>;
  approvalChain: Array<{
  step: number;
  name: string;
  permission: string;
  requireDistinctFrom: Array<"preparer">;
  preparerId: string | null;
  satisfiedBy: string | null;
  satisfiedAt: string | null;
}>;
};
export const TemplateSchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["draft", "in_review", "published", "archived"] as const),
  publishedVersion: z.number().int().nullable(),
  draftVersion: z.number().int().nullable(),
  revision: z.number().int(),
  origin: z.enum(["built", "imported", "starter", "agent"] as const),
  domainKey: z.string().nullable(),
  archivedAt: z.string().nullable(),
  archivedBy: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  versions: z.array(TemplateVersionSchema),
  reviewRequired: z.boolean(),
  submittedBy: z.string().nullable(),
  submittedAt: z.string().nullable(),
  reviewNote: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  actions: z.array(TemplateActionSchema),
  approvalChain: z.array(z.object({
    step: z.number().int().min(1),
    name: z.string(),
    permission: z.string(),
    requireDistinctFrom: z.array(z.enum(["preparer"] as const)),
    preparerId: z.string().nullable(),
    satisfiedBy: z.string().nullable(),
    satisfiedAt: z.string().nullable(),
  }).strict()),
}).strict();

export type TemplateListItem = {
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "in_review" | "published" | "archived";
  origin: "built" | "imported" | "starter" | "agent";
  domainKey: string | null;
  publishedVersion: number | null;
  revision: number;
  isDefault: boolean;
  tags: Array<string>;
  sectionNames: Array<string>;
  versionCount: number;
  usedByTypeCount: number;
  actions: Array<TemplateAction>;
  updatedAt: string;
};
export const TemplateListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["draft", "in_review", "published", "archived"] as const),
  origin: z.enum(["built", "imported", "starter", "agent"] as const),
  domainKey: z.string().nullable(),
  publishedVersion: z.number().int().nullable(),
  revision: z.number().int(),
  isDefault: z.boolean(),
  tags: z.array(z.string()),
  sectionNames: z.array(z.string()),
  versionCount: z.number().int(),
  usedByTypeCount: z.number().int(),
  actions: z.array(TemplateActionSchema),
  updatedAt: z.string(),
}).strict();

export type TemplateRevisionConflict = {
  reason: "revision_conflict";
  expectedRevision: number;
  currentRevision: number;
  currentVersion: number | null;
  comparisonPath: string;
};
export const TemplateRevisionConflictSchema = z.object({
  reason: z.literal("revision_conflict"),
  expectedRevision: z.number().int(),
  currentRevision: z.number().int(),
  currentVersion: z.number().int().nullable(),
  comparisonPath: z.string(),
}).strict();

export type TemplateTransitionRefusal = {
  reason: "illegal_transition";
  from: "draft" | "in_review" | "published" | "archived";
  action: "edit" | "submit" | "approve" | "reject" | "publish" | "archive" | "restore";
  detail: string;
  allowedActions: Array<"edit" | "submit" | "approve" | "reject" | "publish" | "archive" | "restore">;
};
export const TemplateTransitionRefusalSchema = z.object({
  reason: z.literal("illegal_transition"),
  from: z.enum(["draft", "in_review", "published", "archived"] as const),
  action: z.enum(["edit", "submit", "approve", "reject", "publish", "archive", "restore"] as const),
  detail: z.string(),
  allowedActions: z.array(z.enum(["edit", "submit", "approve", "reject", "publish", "archive", "restore"] as const)),
}).strict();

export type TemplateSubmitRefusal = {
  reason: "template_invalid";
  issues: Array<string>;
  types: Array<{
  typeId: string;
  typeName: string;
  active: boolean;
  missingFields: Array<string>;
}>;
};
export const TemplateSubmitRefusalSchema = z.object({
  reason: z.literal("template_invalid"),
  issues: z.array(z.string()),
  types: z.array(z.object({
    typeId: z.string(),
    typeName: z.string(),
    active: z.boolean(),
    missingFields: z.array(z.string()),
  }).strict()),
}).strict();

export type TemplateArchiveBlocked = {
  reason: "linked_active_types";
  types: Array<{
  typeId: string;
  typeName: string;
  requiredFields: Array<string>;
}>;
  replacements: Array<{
  templateId: string;
  name: string;
  publishedVersion: number | null;
}>;
};
export const TemplateArchiveBlockedSchema = z.object({
  reason: z.literal("linked_active_types"),
  types: z.array(z.object({
    typeId: z.string(),
    typeName: z.string(),
    requiredFields: z.array(z.string()),
  }).strict()),
  replacements: z.array(z.object({
    templateId: z.string(),
    name: z.string(),
    publishedVersion: z.number().int().nullable(),
  }).strict()),
}).strict();

export type TemplateEvidenceAlternative = {
  type: string;
  legacyType: string;
  label: string;
  description: string;
};
export const TemplateEvidenceAlternativeSchema = z.object({
  type: z.string(),
  legacyType: z.string(),
  label: z.string(),
  description: z.string(),
}).strict();

export type TemplateUnsupportedEvidenceField = {
  sectionId: string | null;
  sectionName: string;
  fieldId: string | null;
  fieldKey: string;
  fieldLabel: string;
  fieldType: string;
  evidence: string;
  requirement: "always" | "conditional";
};
export const TemplateUnsupportedEvidenceFieldSchema = z.object({
  sectionId: z.string().nullable(),
  sectionName: z.string(),
  fieldId: z.string().nullable(),
  fieldKey: z.string(),
  fieldLabel: z.string(),
  fieldType: z.string(),
  evidence: z.string(),
  requirement: z.enum(["always", "conditional"] as const),
}).strict();

export type TemplateEvidenceRefusal = {
  reason: "unsupported_evidence";
  fields: Array<TemplateUnsupportedEvidenceField>;
  evidence: Array<{
  kind: string;
  label: string;
  detail: string;
  alternatives: Array<TemplateEvidenceAlternative>;
}>;
  unblockedByStory: string;
};
export const TemplateEvidenceRefusalSchema = z.object({
  reason: z.literal("unsupported_evidence"),
  fields: z.array(TemplateUnsupportedEvidenceFieldSchema),
  evidence: z.array(z.object({
    kind: z.string(),
    label: z.string(),
    detail: z.string(),
    alternatives: z.array(TemplateEvidenceAlternativeSchema),
  }).strict()),
  unblockedByStory: z.string(),
}).strict();

export type PublishTemplateBody = {
  expectedRevision?: number | undefined;
  changeNote?: string | null | undefined;
};
export const PublishTemplateBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  changeNote: z.string().max(2000).nullable().optional(),
}).strict();

export type ArchiveTemplateBody = {
  expectedRevision?: number | undefined;
  reason?: string | null | undefined;
  replacementTemplateId?: string | null | undefined;
};
export const ArchiveTemplateBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  reason: z.string().max(500).nullable().optional(),
  replacementTemplateId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").nullable().optional(),
}).strict();

export type RestoreTemplateVersionBody = {
  expectedRevision?: number | undefined;
  changeNote?: string | null | undefined;
};
export const RestoreTemplateVersionBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  changeNote: z.string().max(2000).nullable().optional(),
}).strict();

export type SubmitTemplateBody = {
  expectedRevision?: number | undefined;
  note?: string | null | undefined;
};
export const SubmitTemplateBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  note: z.string().max(2000).nullable().optional(),
}).strict();

export type ApproveTemplateBody = {
  expectedRevision?: number | undefined;
  changeNote?: string | null | undefined;
};
export const ApproveTemplateBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  changeNote: z.string().max(2000).nullable().optional(),
}).strict();

export type RejectTemplateBody = {
  expectedRevision?: number | undefined;
  note: string;
};
export const RejectTemplateBodySchema = z.object({
  expectedRevision: z.number().int().min(0).optional(),
  note: z.string().max(2000).refine((value) => value.trim().length > 0),
}).strict();

export type TemplateDraftSection = {
  name: string;
  description: string | null;
  fields: Array<{
  key: string;
  label: string;
  type: string;
  required: boolean;
}>;
};
export const TemplateDraftSectionSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  fields: z.array(z.object({
    key: z.string(),
    label: z.string(),
    type: z.string(),
    required: z.boolean(),
  }).strict()),
}).strict();

export type TemplateStarter = {
  key: string;
  name: string;
  description: string;
  domainKey: string;
  starterVersion: number;
  provenance: string;
  sections: Array<TemplateDraftSection>;
  fieldCount: number;
};
export const TemplateStarterSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  domainKey: z.string(),
  starterVersion: z.number().int(),
  provenance: z.string(),
  sections: z.array(TemplateDraftSectionSchema),
  fieldCount: z.number().int(),
}).strict();

export type TemplateDraftPreview = {
  previewId: string;
  source: "starter" | "agent" | "import" | "blank" | "duplicate";
  origin: "built" | "imported" | "starter" | "agent";
  name: string;
  description: string | null;
  domainKey: string;
  provenance: string;
  sections: Array<TemplateDraftSection>;
  fieldCount: number;
  mapped: Array<string>;
  omitted: Array<string>;
  invalid: Array<string>;
  warnings: Array<string>;
  expiresAt: string;
};
export const TemplateDraftPreviewSchema = z.object({
  previewId: z.string(),
  source: z.enum(["starter", "agent", "import", "blank", "duplicate"] as const),
  origin: z.enum(["built", "imported", "starter", "agent"] as const),
  name: z.string(),
  description: z.string().nullable(),
  domainKey: z.string(),
  provenance: z.string(),
  sections: z.array(TemplateDraftSectionSchema),
  fieldCount: z.number().int(),
  mapped: z.array(z.string()),
  omitted: z.array(z.string()),
  invalid: z.array(z.string()),
  warnings: z.array(z.string()),
  expiresAt: z.string(),
}).strict();

export type TemplateDraftBrief = {
  purpose: string;
  sections?: string | null | undefined;
  evidence?: string | null | undefined;
  reviewerIntent?: string | null | undefined;
};
export const TemplateDraftBriefSchema = z.object({
  purpose: z.string().min(20, "Describe what this template is for in a sentence or two — the model has nothing to work from otherwise").max(2000),
  sections: z.string().max(2000).nullable().optional(),
  evidence: z.string().max(1000).nullable().optional(),
  reviewerIntent: z.string().max(1000).nullable().optional(),
}).strict();

export type TemplateDraftIdentity = {
  name: string;
  description?: string | null | undefined;
  domainKey: string;
};
export const TemplateDraftIdentitySchema = z.object({
  name: z.string().min(1, "A template needs a name").max(120),
  description: z.string().max(400).nullable().optional(),
  domainKey: z.string().min(1, "Choose the domain this template belongs to").max(60),
}).strict();

export type PreviewTemplateDraftBody = {
  name: string;
  description?: string | null | undefined;
  domainKey: string;
  source: "starter" | "agent" | "import" | "blank" | "duplicate";
  starterKey?: string | null | undefined;
  sourceTemplateId?: string | null | undefined;
  importId?: string | null | undefined;
  brief?: TemplateDraftBrief | null | undefined;
};
export const PreviewTemplateDraftBodySchema = z.object({
  name: z.string().min(1, "A template needs a name").max(120),
  description: z.string().max(400).nullable().optional(),
  domainKey: z.string().min(1, "Choose the domain this template belongs to").max(60),
  source: z.enum(["starter", "agent", "import", "blank", "duplicate"] as const),
  starterKey: z.string().min(1).max(60).nullable().optional(),
  sourceTemplateId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").nullable().optional(),
  importId: z.string().min(8).max(64).nullable().optional(),
  brief: TemplateDraftBriefSchema.nullable().optional(),
}).strict().refine((b) => b.source !== 'starter' || !!b.starterKey).refine((b) => b.source !== 'duplicate' || !!b.sourceTemplateId).refine((b) => b.source !== 'import' || !!b.importId).refine((b) => b.source !== 'agent' || !!b.brief);

export type CreateTemplateBody = {
  previewId: string;
  requestKey: string;
  name?: string | null | undefined;
};
export const CreateTemplateBodySchema = z.object({
  previewId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  requestKey: z.string().min(8, "requestKey must be at least 8 characters").max(64),
  name: z.string().min(1, "A template needs a name").max(120).nullable().optional(),
}).strict();

export type SaveTemplateDraftBody = {
  schema: Record<string, unknown>;
  name?: string | null | undefined;
  description?: string | null | undefined;
  changeNote?: string | null | undefined;
  expectedRevision: number;
};
export const SaveTemplateDraftBodySchema = z.object({
  schema: z.record(z.unknown()),
  name: z.string().min(1, "A template needs a name").max(120).nullable().optional(),
  description: z.string().max(400).nullable().optional(),
  changeNote: z.string().max(2000).nullable().optional(),
  expectedRevision: z.number().int().min(0),
}).strict();

export type TemplateDraftAck = {
  template: Template;
  acknowledgedAt: string;
};
export const TemplateDraftAckSchema = z.object({
  template: TemplateSchema,
  acknowledgedAt: z.string(),
}).strict();

export type ListTemplatesResponse = {
  items: Array<TemplateListItem>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListTemplatesQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  status: z.enum(["draft", "in_review", "published", "archived"] as const).optional(),
  domain: z.string().min(1).optional(),
  origin: z.enum(["built", "imported", "starter", "agent"] as const).optional(),
});

export async function listTemplates(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
  status?: "draft" | "in_review" | "published" | "archived" | undefined;
  domain?: string | undefined;
  origin?: "built" | "imported" | "starter" | "agent" | undefined;
}): Promise<ListTemplatesResponse> {
  const response = await api.get<ListTemplatesResponse>(`/report-presets`, { params: query });
  return response.data;
}

export type GetTemplateStartersResponse = {
  data: Array<TemplateStarter>;
};

export async function getTemplateStarters(): Promise<GetTemplateStartersResponse> {
  const response = await api.get<GetTemplateStartersResponse>(`/report-presets/starters`);
  return response.data;
}

export type PreviewTemplateDraftResponse = {
  data: TemplateDraftPreview;
};

export async function previewTemplateDraft(body: PreviewTemplateDraftBody): Promise<PreviewTemplateDraftResponse> {
  const response = await api.post<PreviewTemplateDraftResponse>(`/report-presets/drafts/preview`, body);
  return response.data;
}

export type CreateTemplateResponse = {
  data: Template;
};

export async function createTemplate(body: CreateTemplateBody): Promise<CreateTemplateResponse> {
  const response = await api.post<CreateTemplateResponse>(`/report-presets/drafts`, body);
  return response.data;
}

export type SaveTemplateDraftResponse = {
  data: TemplateDraftAck;
};

export const SaveTemplateDraftParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function saveTemplateDraft(id: string, body: SaveTemplateDraftBody): Promise<SaveTemplateDraftResponse> {
  const response = await api.patch<SaveTemplateDraftResponse>(`/report-presets/${encodeURIComponent(id)}/draft`, body);
  return response.data;
}

export type GetTemplateLifecycleResponse = {
  data: Template;
};

export const GetTemplateLifecycleParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getTemplateLifecycle(id: string): Promise<GetTemplateLifecycleResponse> {
  const response = await api.get<GetTemplateLifecycleResponse>(`/report-presets/${encodeURIComponent(id)}/lifecycle`);
  return response.data;
}

export type ListTemplateVersionsResponse = {
  data: Array<TemplateVersion>;
};

export const ListTemplateVersionsParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listTemplateVersions(id: string): Promise<ListTemplateVersionsResponse> {
  const response = await api.get<ListTemplateVersionsResponse>(`/report-presets/${encodeURIComponent(id)}/versions`);
  return response.data;
}

export type GetTemplateVersionResponse = {
  data: TemplateVersionDetail;
};

export const GetTemplateVersionParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  version: z.coerce.number().int().min(1),
});

export async function getTemplateVersion(id: string, version: number): Promise<GetTemplateVersionResponse> {
  const response = await api.get<GetTemplateVersionResponse>(`/report-presets/${encodeURIComponent(id)}/versions/${encodeURIComponent(version)}`);
  return response.data;
}

export type PublishTemplateResponse = {
  data: Template;
};

export const PublishTemplateParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function publishTemplate(id: string, body: PublishTemplateBody): Promise<PublishTemplateResponse> {
  const response = await api.post<PublishTemplateResponse>(`/report-presets/${encodeURIComponent(id)}/publish`, body);
  return response.data;
}

export type ArchiveTemplateResponse = {
  data: Template;
};

export const ArchiveTemplateParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function archiveTemplate(id: string, body: ArchiveTemplateBody): Promise<ArchiveTemplateResponse> {
  const response = await api.post<ArchiveTemplateResponse>(`/report-presets/${encodeURIComponent(id)}/archive`, body);
  return response.data;
}

export type SubmitTemplateResponse = {
  data: Template;
};

export const SubmitTemplateParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function submitTemplate(id: string, body: SubmitTemplateBody): Promise<SubmitTemplateResponse> {
  const response = await api.post<SubmitTemplateResponse>(`/report-presets/${encodeURIComponent(id)}/submit`, body);
  return response.data;
}

export type ApproveTemplateResponse = {
  data: Template;
};

export const ApproveTemplateParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function approveTemplate(id: string, body: ApproveTemplateBody): Promise<ApproveTemplateResponse> {
  const response = await api.post<ApproveTemplateResponse>(`/report-presets/${encodeURIComponent(id)}/approve`, body);
  return response.data;
}

export type RejectTemplateResponse = {
  data: Template;
};

export const RejectTemplateParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function rejectTemplate(id: string, body: RejectTemplateBody): Promise<RejectTemplateResponse> {
  const response = await api.post<RejectTemplateResponse>(`/report-presets/${encodeURIComponent(id)}/reject`, body);
  return response.data;
}

export type RestoreTemplateVersionResponse = {
  data: Template;
};

export const RestoreTemplateVersionParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  version: z.coerce.number().int().min(1),
});

export async function restoreTemplateVersion(id: string, version: number, body: RestoreTemplateVersionBody): Promise<RestoreTemplateVersionResponse> {
  const response = await api.post<RestoreTemplateVersionResponse>(`/report-presets/${encodeURIComponent(id)}/versions/${encodeURIComponent(version)}/restore`, body);
  return response.data;
}

export const templatesKeys = {
  all: ["templates"] as const,
  lists: () => [...templatesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...templatesKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...templatesKeys.all, 'detail'] as const,
  detail: (id: string) => [...templatesKeys.details(), id] as const,
};
